import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, thumbUrl } from "../../lib/api";
import { useToast } from "../../lib/toast";

// Admin → Homepage Spotlight: choose which live artists appear in the three
// fanned cards on the homepage hero, and in what order. Three show at a time;
// more picks rotate every 5 seconds.
function ArtistRow({ artist, children }) {
  return (
    <div
      className="card"
      style={{ display: "grid", gridTemplateColumns: "48px minmax(0, 1fr) auto", gap: "var(--space-3)", alignItems: "center", padding: "var(--space-2) var(--space-3)" }}
    >
      {artist.profile_image
        ? <img src={thumbUrl(artist.profile_image)} alt="" loading="lazy" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8 }} />
        : <div style={{ width: 48, height: 48, borderRadius: 8, background: "var(--bg3)" }} aria-hidden="true" />}
      <div style={{ minWidth: 0 }}>
        <strong>{artist.stage_name || "Unnamed artist"}</strong>
        <div className="text-muted fs-12">{[artist.category, artist.city].filter(Boolean).join(" · ")}</div>
        {artist.is_public === false && <div className="text-red fs-11">Not live: hidden from the homepage until they go live</div>}
      </div>
      <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>{children}</div>
    </div>
  );
}

export default function AdminHomepageSpotlight() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [picks, setPicks] = useState([]);
  const [boostsFirst, setBoostsFirst] = useState(true);
  const [paidBoosts, setPaidBoosts] = useState([]);
  const [maxPicks, setMaxPicks] = useState(9);
  const [dirty, setDirty] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/homepage/spotlight");
      setPicks(data.artists || []);
      setBoostsFirst(data.boosts_first !== false);
      setPaidBoosts(data.paid_boosts || []);
      setMaxPicks(data.max_picks || 9);
      setDirty(false);
    } catch (error) {
      toast(`Could not load the homepage spotlight: ${formatApiError(error)}`, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Debounced artist search (live, non-suspended artists only).
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/admin/homepage/spotlight/search", { params: { q: query } });
        if (!cancelled) setResults(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) toast(formatApiError(error), "error");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, toast]);

  const change = (next) => { setPicks(next); setDirty(true); };
  const add = (artist) => {
    if (picks.some((pick) => pick.user_id === artist.user_id)) return;
    if (picks.length >= maxPicks) { toast(`You can pick up to ${maxPicks} artists.`, "error"); return; }
    change([...picks, { ...artist, is_public: true }]);
  };
  const remove = (userId) => change(picks.filter((pick) => pick.user_id !== userId));
  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= picks.length) return;
    const next = [...picks];
    [next[index], next[target]] = [next[target], next[index]];
    change(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/homepage/spotlight", {
        artist_ids: picks.filter((pick) => pick.is_public !== false).map((pick) => pick.user_id),
        boosts_first: boostsFirst,
      });
      toast("Homepage spotlight saved.", "success");
      await load();
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setSaving(false);
    }
  };

  const pickedIds = new Set(picks.map((pick) => pick.user_id));

  return (
    <div className="pad-24" data-testid="admin-homepage-spotlight">
      <h2 className="font-serif fs-24 fw-700 mb-8">Homepage Spotlight</h2>
      <p className="text-muted fs-13 mb-16">
        Choose the artists shown in the three cards on the homepage hero. Three show at a time; if you pick more, they rotate every 5 seconds.
        Empty slots are filled with featured, then top-rated, live artists.
      </p>

      <div className="card card-pad mb-16">
        <div className="mb-12" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap" }}>
          <h3 className="fs-16 fw-700">Your picks ({picks.length}/{maxPicks})</h3>
          <button type="button" className="btn btn-gold btn-sm" onClick={save} disabled={saving || !dirty} data-testid="spotlight-save">
            {saving ? "Saving…" : dirty ? "Save spotlight" : "Saved"}
          </button>
        </div>
        {loading ? (
          <p className="text-muted fs-13">Loading…</p>
        ) : picks.length === 0 ? (
          <p className="text-muted fs-13">No picks yet. The homepage uses paid boosts, then featured and top-rated artists. Add artists below.</p>
        ) : (
          <div style={{ display: "grid", gap: "var(--space-2)" }}>
            {picks.map((artist, index) => (
              <ArtistRow key={artist.user_id} artist={artist}>
                <span className="text-muted fs-11">#{index + 1}</span>
                <button type="button" className="btn btn-ghost btn-xs" onClick={() => move(index, -1)} disabled={index === 0}>Move up</button>
                <button type="button" className="btn btn-ghost btn-xs" onClick={() => move(index, 1)} disabled={index === picks.length - 1}>Move down</button>
                <button type="button" className="btn btn-red btn-xs" onClick={() => remove(artist.user_id)} data-testid={`spotlight-remove-${artist.user_id}`}>Remove</button>
              </ArtistRow>
            ))}
          </div>
        )}

        <div className="mt-16">
          <div className="field-label mb-8">Paid Homepage Banner boosts</div>
          <label className="fs-13" style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-2)", cursor: "pointer" }}>
            <input type="radio" name="spotlight-order" checked={boostsFirst} onChange={() => { setBoostsFirst(true); setDirty(true); }} data-testid="spotlight-boosts-first" />
            Show paid boosts before my picks (recommended: they paid for this placement)
          </label>
          <label className="fs-13" style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: "pointer" }}>
            <input type="radio" name="spotlight-order" checked={!boostsFirst} onChange={() => { setBoostsFirst(false); setDirty(true); }} data-testid="spotlight-picks-first" />
            Show my picks first, then paid boosts
          </label>
          <p className="text-muted fs-12 mt-8">
            {paidBoosts.length
              ? `Active paid boosts: ${paidBoosts.map((artist) => artist.stage_name).join(", ")}.`
              : "No artist has an active paid Homepage Banner boost right now."}
          </p>
        </div>
      </div>

      <div className="card card-pad">
        <h3 className="fs-16 fw-700 mb-8">Add artists</h3>
        <input
          className="field-input mb-12"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search live artists by name, category or city"
          data-testid="spotlight-search"
        />
        {searching && results.length === 0 ? (
          <p className="text-muted fs-13">Searching…</p>
        ) : results.length === 0 ? (
          <p className="text-muted fs-13">No live artists match.</p>
        ) : (
          <div style={{ display: "grid", gap: "var(--space-2)" }}>
            {results.map((artist) => (
              <ArtistRow key={artist.user_id} artist={artist}>
                <button
                  type="button"
                  className="btn btn-gold btn-xs"
                  onClick={() => add(artist)}
                  disabled={pickedIds.has(artist.user_id)}
                  data-testid={`spotlight-add-${artist.user_id}`}
                >
                  {pickedIds.has(artist.user_id) ? "Added" : "Add"}
                </button>
              </ArtistRow>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
